import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, LayoutDashboard, LogOut, Search, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";

const NAV = [
  { label: "Vestidos", to: "/loja?cat=Vestidos" },
  { label: "Blazers", to: "/loja?cat=Blazers" },
  { label: "Tricô", to: "/loja?cat=Tricô" },
  { label: "Calças", to: "/loja?cat=Calças" },
  { label: "Camisas", to: "/loja?cat=Camisas" },
];

const ANNOUNCE = [
  "Frete grátis acima de R$ 499",
  "Até 6x sem juros",
  "Novos ingressos toda semana",
  "Embalagem presente cortesia",
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50">
      {/* announcement bar */}
      <div className="bg-[#121212] text-[#F8F7F4] h-9 flex items-center overflow-hidden">
        <div className="marquee flex whitespace-nowrap">
          {[...ANNOUNCE, ...ANNOUNCE, ...ANNOUNCE, ...ANNOUNCE].map((t, i) => (
            <span key={i} className="mx-10 text-[10px] uppercase tracking-[0.25em] text-[#F8F7F4]/80">{t}</span>
          ))}
        </div>
      </div>

      {/* nav */}
      <div className={`transition-all duration-500 ${scrolled ? "bg-[#F8F7F4]/85 backdrop-blur-md border-b border-[#E6E5E1]" : "bg-[#F8F7F4]"}`}>
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-[72px] grid grid-cols-3 items-center">
          {/* left nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.slice(0, 3).map((n) => (
              <Link key={n.label} to={n.to} data-testid={`nav-${n.label}`}
                className="nav-link text-[11px] uppercase tracking-[0.15em] text-[#1A1A1A]/80 hover:text-[#1A1A1A] transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-[#1A1A1A] justify-self-start" onClick={() => setOpen(!open)}>
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>

          {/* centered logo */}
          <Link to="/" data-testid="logo-link" className="justify-self-center font-serif text-3xl md:text-[2rem] leading-none tracking-tight text-[#1A1A1A]">
            ATELIÊ
          </Link>

          {/* right icons */}
          <div className="flex items-center gap-5 md:gap-6 justify-self-end">
            <nav className="hidden lg:flex items-center gap-8 mr-2">
              {NAV.slice(3).map((n) => (
                <Link key={n.label} to={n.to} data-testid={`nav-${n.label}`}
                  className="nav-link text-[11px] uppercase tracking-[0.15em] text-[#1A1A1A]/80 hover:text-[#1A1A1A] transition-colors">
                  {n.label}
                </Link>
              ))}
            </nav>
            <Link to="/loja" data-testid="search-link" className="hidden md:block text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
              <Search size={19} strokeWidth={1.5} />
            </Link>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="admin-link" className="text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                    <LayoutDashboard size={19} strokeWidth={1.5} />
                  </Link>
                )}
                <Link to="/favoritos" data-testid="wishlist-link" className="relative text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                  <Heart size={19} strokeWidth={1.5} />
                  {wishCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#B89C65] text-[#121212] text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full">{wishCount}</span>
                  )}
                </Link>
                <Link to="/conta" data-testid="account-link" className="text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                  <User size={19} strokeWidth={1.5} />
                </Link>
                <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="hidden md:block text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                  <LogOut size={19} strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="login-link" className="text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                <User size={19} strokeWidth={1.5} />
              </Link>
            )}
            <button data-testid="cart-link" onClick={() => setDrawerOpen(true)} className="relative text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
              <ShoppingBag size={19} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#B89C65] text-[#121212] text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#FFFFFF] border-t border-[#E6E5E1] overflow-hidden">
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} onClick={() => setOpen(false)}
                  className="text-[11px] uppercase tracking-[0.15em] text-[#1A1A1A]/80" data-testid={`mobile-nav-${n.label}`}>{n.label}</Link>
              ))}
              <div className="h-px bg-[#E6E5E1] my-1" />
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em]">Admin</Link>}
                  <Link to="/conta" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em]">Minha Conta</Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-[11px] uppercase tracking-[0.15em] text-left text-[#B89C65]">Sair</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-[0.15em]">Entrar</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
